import React, { FC, useState, SyntheticEvent, useCallback } from "react";
import Modal from "composants/Modal/Modal";
import { ComponentState } from "types/Component";
import { produce } from "immer";
import { Trans } from "react-i18next";
import { t } from "utils/i18n";
import { GroupByFunctionParameter } from "types/InteractiveReport";

interface GroupByDialogProps {
  columns: ComponentState[];
  initialGroupBy: string[];
  initialFunctionList: GroupByFunctionParameter[];
  initialDefaultOpenBreakRows?: boolean;
  useLabel: boolean;
  onClose(): void;
  onValidate(
    groupBy: string[],
    functionList: GroupByFunctionParameter[],
    defaultOpenBreakRows: boolean,
  ): void;
}

const SUPPORTED_FUNCTION = [
  "SUM",
  "AVG",
  "MAX",
  "MIN",
  "MEDIAN",
  "COUNT",
  "COUNT_DISTINCT",
  "RATIO_TO_REPORT_SUM",
  "RATIO_TO_REPORT_COUNT",
];

export function useGroupByColumns() {
  const [groupBy, setGroupByColumns] = useState<string[]>([]);
  const [functionList, setFunctionList] = useState<GroupByFunctionParameter[]>([]);
  const [defaultOpenBreakRows, setDefaultOpenBreakRows] = useState(false);

  const [isGroupByDialogOpen, setGroupByDialogOpen] = useState(false);

  const toggle = useCallback(() => setGroupByDialogOpen((val) => !val), [isGroupByDialogOpen]);

  const setColumns = useCallback((col: string[]) => setGroupByColumns(col), [groupBy]);

  return {
    columns: groupBy,
    functionList,
    defaultOpenBreakRows,
    setColumns,
    setFunctionList,
    setDefaultOpenBreakRows,
    isOpen: isGroupByDialogOpen,
    toggle,
  };
}

const initialFunctionParamater: GroupByFunctionParameter = {
  functionType: SUPPORTED_FUNCTION[0],
  column: "",
  label: "",
};

const GroupByFunctionList: FC<{
  groupBy: string[];
  functionList: GroupByFunctionParameter[];
  columns: ComponentState[];
  useLabel: boolean;
  onChange(e: SyntheticEvent<HTMLSelectElement | HTMLInputElement>): void;
  addNewLine(): void;
  deleteLine(index: number): void;
}> = ({ groupBy, functionList, columns, useLabel, onChange, addNewLine, deleteLine }) => {
  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>{t("commun_fonction")}</th>
            <th>{t("commun_colonne")}</th>
            {useLabel && <th>{t("commun_libelle")}</th>}
          </tr>
        </thead>

        <tbody>
          {functionList.map((fn, index) => {
            const columnsUsedInOtherRows = functionList
              .filter((_, i) => i !== index)
              .map((other) => other.column)
              .filter((column) => column !== "");

            const availableColumns = columns.filter(
              (col) =>
                !columnsUsedInOtherRows.includes(col.column) &&
                (col.typeCompo === "NUMBER" || col.typeCompo === "INUM") &&
                !groupBy.includes(col.column),
            );

            return (
              <tr key={index}>
                <td>
                  <div className="select is-small">
                    <select
                      name="functionType"
                      value={fn.functionType}
                      onChange={onChange}
                      data-index={index}
                    >
                      <option value="" />
                      {SUPPORTED_FUNCTION.map((fnSupported) => {
                        return (
                          <option key={fnSupported} value={fnSupported}>
                            {t(`commun_aggrate_function_${fnSupported.toLowerCase()}`)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </td>
                <td>
                  <div className="select is-small">
                    <select name="column" value={fn.column} onChange={onChange} data-index={index}>
                      <option value="" />
                      {availableColumns.map((col) => {
                        return (
                          <option key={col.column} value={col.column}>
                            {col.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </td>
                {useLabel && (
                  <td>
                    <input
                      className="input is-small"
                      type="text"
                      name="label"
                      value={fn.label}
                      onChange={onChange}
                      data-index={index}
                    />
                  </td>
                )}
                <td>
                  <button
                    className="delete"
                    onClick={() => deleteLine(index)}
                    title={t("commun_supprimer")}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button className="button is-text is-small" onClick={addNewLine}>
        Ajouter une autre fonction
      </button>
    </>
  );
};

const GroupByDialog: FC<GroupByDialogProps> = (props) => {
  const [groupBy, setBreakRow] = useState<string[]>(props.initialGroupBy || []);

  const [fnList, setFnList] = useState(
    props.initialFunctionList.length <= 0 ? [initialFunctionParamater] : props.initialFunctionList,
  );

  const [defaultOpenBreakRows, setDefaultOpenBreakRows] = useState(
    props.initialDefaultOpenBreakRows ?? false,
  );

  function onChangeFnList(e: SyntheticEvent<HTMLSelectElement | HTMLInputElement>) {
    setFnList(
      produce(fnList, (draft) => {
        const currentIndex = e.currentTarget.dataset.index;
        const field = e.currentTarget.name;

        if (currentIndex && field) {
          draft[parseInt(currentIndex, 10)][field] = e.currentTarget.value;
        }
      }),
    );
  }

  function addNewLineFnList() {
    setFnList(
      produce(fnList, (draft) => {
        draft.push(initialFunctionParamater);
      }),
    );
  }

  function deleteLineFnList(index: number) {
    setFnList(
      produce(fnList, (draft) => {
        if (fnList.length === 1) {
          draft.splice(index, 1, initialFunctionParamater);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  }

  function onChange(e: SyntheticEvent<HTMLSelectElement>) {
    const newState = produce(groupBy, (draft) => {
      draft.push(e.currentTarget.value);
    });

    setBreakRow(newState);
  }

  function onChangeExisting(e: SyntheticEvent<HTMLSelectElement>) {
    const oldValue = e.currentTarget.name;
    const value = e.currentTarget.value;

    const index = groupBy.indexOf(oldValue);
    if (index !== -1) {
      if (value === "") {
        onDelete(oldValue);
      } else {
        const newState = produce(groupBy, (draft) => {
          draft[index] = value;
        });
        setBreakRow(newState);
      }
    }
  }

  function onDelete(col: string) {
    const newState = produce(groupBy, (draft) => {
      const index = draft.indexOf(col);
      if (index !== -1) {
        draft.splice(index, 1);
      }
    });
    setBreakRow(newState);
  }

  function onValidate() {
    let finalFnList = fnList.filter((fn) => fn.column !== "");
    props.onValidate(groupBy, finalFnList, defaultOpenBreakRows);
  }

  function clear() {
    setBreakRow([]);
    setFnList([initialFunctionParamater]);
    setDefaultOpenBreakRows(false);
  }

  const optionsExisting = props.columns
    .filter((column) => column.typeCompo !== "OEL")
    .map((column) => {
      return (
        <option key={column.column} value={column.column}>
          {column.label}
        </option>
      );
    });

  const optionsNew = props.columns
    .filter((column) => column.typeCompo !== "OEL")
    .filter((column) => groupBy.indexOf(column.column) === -1)
    .map((column) => {
      return (
        <option key={column.column} value={column.column}>
          {column.label}
        </option>
      );
    });

  return (
    <Modal
      onClose={props.onClose}
      onValidate={onValidate}
      title={
        <>
          <Trans i18nKey="commun_ajouter_un_regroupement">Ajouter un regroupement</Trans>
          <span className="is-pulled-right">
            <button className="button is-text is-small" onClick={clear}>
              <Trans i18nKey="commun_vider" />
            </button>
          </span>
        </>
      }
      hideCancel
    >
      {groupBy.map((col) => {
        return (
          <div key={col}>
            <div className="select mb-5">
              <select name={col} value={col} onChange={onChangeExisting}>
                <option value="" />
                {optionsExisting}
              </select>
            </div>
          </div>
        );
      })}
      <hr style={{ backgroundColor: "hsl(0, 0%, 94%)", width: "40%", margin: "1.25rem auto" }} />
      <div className="select">
        <select value="" onChange={onChange}>
          <option value="" />
          {optionsNew}
        </select>
      </div>

      <hr style={{ backgroundColor: "hsl(0, 0%, 90%)", width: "40%", margin: "1.25rem auto" }} />

      <GroupByFunctionList
        groupBy={groupBy}
        columns={props.columns.filter((c: any) => c.compoVisible)}
        functionList={fnList}
        useLabel={props.useLabel}
        onChange={onChangeFnList}
        addNewLine={addNewLineFnList}
        deleteLine={deleteLineFnList}
      />

      <div className="mt-4">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={defaultOpenBreakRows}
            onChange={(e) => setDefaultOpenBreakRows(e.currentTarget.checked)}
          />
          <Trans i18nKey="commun_ouvrir_groupes_par_defaut">
            Ouvrir les groupes par défaut
          </Trans>
        </label>
      </div>
    </Modal>
  );
};

export default GroupByDialog;
